import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'user' | 'instructor' | 'admin' | 'parent';

export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching user role:', error);
          setRole('user'); // Default to user role
        } else {
          const roles = (data as { role: string }[] | null)?.map(r => r.role) || [];

          // Role priority: admin > instructor > parent > user
          let resolvedRole: UserRole = 'user';
          if (roles.includes('admin')) resolvedRole = 'admin';
          else if (roles.includes('instructor')) resolvedRole = 'instructor';
          else if (roles.includes('parent')) resolvedRole = 'parent';
          else if (roles.includes('user')) resolvedRole = 'user';

          setRole(resolvedRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRole('user'); // Default to user role
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  return { role, loading };
}